import React from 'react';
import { XIcon, MapPinIcon, NavigationIcon, PhoneIcon, UserIcon, TruckIcon, CreditCardIcon, ExternalLinkIcon } from 'lucide-react';
import { Order, OrderStatus, ORDER_STATUSES, STATUS_CONFIG } from './types';
import { StatusBadge } from './StatusBadge';
import { CONFIG } from '../data/config';
import { Station, getStationItems, itemBelongsToStation, STATIONS } from './StationConfig';
interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  isAdditional?: boolean;
  activeStation?: Station;
}
export function OrderDetailModal({
  order,
  onClose,
  onUpdateStatus,
  isAdditional = false,
  activeStation = 'caja'
}: OrderDetailModalProps) {
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('es-CR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const extractCoords = (url: string | null) => {
    if (!url) return null;
    const match = url.match(/q=([-\d.]+),([-\d.]+)/);
    if (match) return {
      lat: match[1],
      lng: match[2]
    };
    return null;
  };
  const coords = extractCoords(order.location_url);
  return <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-257" />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[90vh] z-50 bg-surface rounded-2xl border border-gray-800 shadow-2xl flex flex-col overflow-hidden" data-id="element-258">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 shrink-0" data-id="element-259">
          <div data-id="element-260">
            <div className="flex items-center gap-3" data-id="element-261">
              <h2 className="font-heading text-2xl text-white tracking-wide" data-id="element-262">
                Pedido #{order.order_number}
              </h2>
              {order.table_number && <span className="px-2 py-1 bg-dorado/15 text-dorado text-xs font-bold rounded-lg border border-dorado/25" data-id="element-263">
                  Mesa {order.table_number}
                </span>}
              {isAdditional && <span className="px-2 py-1 bg-naranja/15 text-naranja text-xs font-bold rounded-lg border border-naranja/25" data-id="element-264">
                  Adicional
                </span>}
            </div>
            <p className="text-sm text-gray-500" data-id="element-265">
              {formatDate(order.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="p-2 bg-surface-light rounded-full text-gray-400 hover:text-white transition-colors" data-id="element-266">
            <XIcon className="w-5 h-5" data-id="element-267" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-268">
          {/* Status + Actions */}
          <div className="flex flex-wrap items-center gap-3" data-id="element-269">
            <StatusBadge status={order.status} data-id="element-270" />
            <div className="flex flex-wrap gap-2 ml-auto" data-id="element-271">
              {ORDER_STATUSES.filter(s => s !== order.status).map(s => {
              const cfg = STATUS_CONFIG[s];
              return <button key={s} onClick={() => onUpdateStatus(order.id, s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:opacity-80 ${cfg.bg} ${cfg.color}`} data-id="element-272">
                    → {cfg.label}
                  </button>;
            })}
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-surface-light rounded-xl p-4 border border-gray-800 space-y-3" data-id="element-273">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider" data-id="element-274">
              Cliente
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-275">
              <div className="flex items-center gap-3" data-id="element-276">
                <div className="p-2 bg-blue-400/10 rounded-lg" data-id="element-277">
                  <UserIcon className="w-4 h-4 text-blue-400" data-id="element-278" />
                </div>
                <div data-id="element-279">
                  <p className="text-xs text-gray-500" data-id="element-280">Nombre</p>
                  <p className="text-white font-medium" data-id="element-281">
                    {order.customer_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-282">
                <div className="p-2 bg-emerald-400/10 rounded-lg" data-id="element-283">
                  <PhoneIcon className="w-4 h-4 text-emerald-400" data-id="element-284" />
                </div>
                <div data-id="element-285">
                  <p className="text-xs text-gray-500" data-id="element-286">Teléfono</p>
                  <a href={`tel:${order.customer_phone}`} className="text-white font-medium hover:text-dorado transition-colors" data-id="element-287">
                    {order.customer_phone}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery + Payment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-288">
            <div className="bg-surface-light rounded-xl p-4 border border-gray-800" data-id="element-289">
              <div className="flex items-center gap-2 mb-2" data-id="element-290">
                <TruckIcon className="w-4 h-4 text-gray-500" data-id="element-291" />
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider" data-id="element-292">
                  Entrega
                </h3>
              </div>
              <p className="text-white font-medium capitalize" data-id="element-293">
                {order.table_number ? `🍽️ Mesa ${order.table_number}` : order.delivery_type === 'express' ? '🛵 Express' : '🏪 Retiro en local'}
              </p>
              {!order.table_number && order.address && <p className="text-sm text-gray-400 mt-1" data-id="element-294">{order.address}</p>}
              {!order.table_number && coords && <div className="flex gap-2 mt-3" data-id="element-295">
                  <a href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-400/10 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-400/20 transition-colors" data-id="element-296">
                    <MapPinIcon className="w-3.5 h-3.5" data-id="element-297" />
                    Google Maps
                    <ExternalLinkIcon className="w-3 h-3" data-id="element-298" />
                  </a>
                  <a href={`https://waze.com/ul?ll=${coords.lat},${coords.lng}&navigate=yes`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-400/10 text-cyan-400 rounded-lg text-xs font-medium hover:bg-cyan-400/20 transition-colors" data-id="element-299">
                    <NavigationIcon className="w-3.5 h-3.5" data-id="element-300" />
                    Waze
                    <ExternalLinkIcon className="w-3 h-3" data-id="element-301" />
                  </a>
                </div>}
            </div>

            <div className="bg-surface-light rounded-xl p-4 border border-gray-800" data-id="element-302">
              <div className="flex items-center gap-2 mb-2" data-id="element-303">
                <CreditCardIcon className="w-4 h-4 text-gray-500" data-id="element-304" />
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider" data-id="element-305">
                  Pago
                </h3>
              </div>
              <p className="text-white font-medium" data-id="element-306">{order.payment_method}</p>
              <div className="mt-3 space-y-1 text-sm" data-id="element-307">
                <div className="flex justify-between" data-id="element-308">
                  <span className="text-gray-500" data-id="element-309">Subtotal</span>
                  <span className="text-gray-300" data-id="element-310">
                    {CONFIG.CURRENCY_SYMBOL}
                    {order.subtotal.toLocaleString()}
                  </span>
                </div>
                {order.express_fee > 0 && <div className="flex justify-between" data-id="element-311">
                    <span className="text-gray-500" data-id="element-312">Express</span>
                    <span className="text-gray-300" data-id="element-313">
                      {CONFIG.CURRENCY_SYMBOL}
                      {order.express_fee.toLocaleString()}
                    </span>
                  </div>}
                <div className="flex justify-between pt-1 border-t border-gray-700" data-id="element-314">
                  <span className="text-white font-semibold" data-id="element-315">Total</span>
                  <span className="text-dorado font-bold text-lg" data-id="element-316">
                    {CONFIG.CURRENCY_SYMBOL}
                    {order.total_final.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Image */}
          {order.image_url && <div className="bg-surface-light rounded-xl p-4 border border-gray-800" data-id="element-317">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-318">
                Ticket
              </h3>
              <img src={order.image_url} alt={`Ticket ${order.order_number}`} className="w-full max-w-xs mx-auto rounded-xl shadow-lg" data-id="element-319" />
            </div>}

          {/* Order Text — filtered by station */}
          <div className="bg-surface-light rounded-xl p-4 border border-gray-800" data-id="element-320">
            <div className="flex items-center justify-between mb-3" data-id="element-321">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider" data-id="element-322">
                {activeStation === 'caja' ? 'Detalle del pedido' : `Items — ${STATIONS.find(s => s.id === activeStation)?.label || ''}`}
              </h3>
              {activeStation !== 'caja' && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${STATIONS.find(s => s.id === activeStation)?.bg} ${STATIONS.find(s => s.id === activeStation)?.color} ${STATIONS.find(s => s.id === activeStation)?.border}`} data-id="element-323">
                  {STATIONS.find(s => s.id === activeStation)?.icon} Solo tu
                  estación
                </span>}
            </div>
            {activeStation === 'caja' ? <pre className="text-sm text-gray-300 whitespace-pre-wrap font-body leading-relaxed" data-id="element-324">
                {order.order_text}
              </pre> : <div className="space-y-3" data-id="element-325">
                {(() => {
              // Parse order_text to show items WITH their details for this station
              const lines = order.order_text.split('\n');
              const itemRegex = /x\d+\s*[—\-]\s*[₡$¢]/;
              const blocks: {
                name: string;
                details: string[];
              }[] = [];
              let lastMatched = false;
              lines.forEach(line => {
                if (itemRegex.test(line)) {
                  const belongs = itemBelongsToStation(line, activeStation);
                  lastMatched = belongs;
                  if (belongs) {
                    blocks.push({
                      name: line.trim(),
                      details: []
                    });
                  }
                } else if (blocks.length > 0 && lastMatched) {
                  const trimmed = line.trim();
                  if (trimmed.length > 0 && !/^(Pedido|Entrega|Total|Cliente|Tel|Pago|Direcc)/i.test(trimmed)) {
                    blocks[blocks.length - 1].details.push(trimmed);
                  }
                }
              });
              return blocks.map((block, i) => <div key={i} className="bg-surface rounded-xl p-3 border border-gray-800" data-id="element-326">
                      <p className="text-sm text-white font-bold" data-id="element-327">
                        {block.name}
                      </p>
                      {block.details.length > 0 && <div className="mt-2 space-y-1 border-t border-gray-700 pt-2" data-id="element-328">
                          {block.details.map((detail, j) => {
                    const cleaned = detail.replace(/^[·•\-\s]+/, '');
                    const colonIdx = cleaned.indexOf(':');
                    if (colonIdx > 0) {
                      const label = cleaned.substring(0, colonIdx).trim();
                      const value = cleaned.substring(colonIdx + 1).trim();
                      return <p key={j} className="text-sm" data-id="element-329">
                                  <span className="text-gray-500" data-id="element-330">
                                    {label}:
                                  </span>{' '}
                                  <span className="text-yellow-300 font-bold" data-id="element-331">
                                    {value}
                                  </span>
                                </p>;
                    }
                    return <p key={j} className="text-sm text-gray-300" data-id="element-332">
                                {cleaned}
                              </p>;
                  })}
                        </div>}
                    </div>);
            })()}
                {order.table_number && <p className="text-xs text-dorado mt-2 pt-2 border-t border-gray-700" data-id="element-333">
                    🍽️ Mesa {order.table_number}
                  </p>}
              </div>}
          </div>
        </div>
      </div>
    </>;
}